import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { usePaperTheme } from '@/hooks/usePaperTheme';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/features/auth/store';
import { getPricing } from '@/services/api/pricing';
import { initializePayment, cancelPlan } from '@/services/api/payments';
import { detectClientCountry } from '@/services/geo';
import { PaymentHistory } from '@/features/billing/components/PaymentHistory';
import { ConfirmDialog } from '@/features/billing/components/ConfirmDialog';
import type { PricingCatalog, PricingPlan, PricingPlanCard } from '@/types/pricing';

export function BillingPage() {
  usePaperTheme();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const currentPlan = user?.plan ?? 'free';
  const expiry = user?.plan_expiry ? new Date(user.plan_expiry) : null;
  const hasActivePaid = useMemo(() => {
    if (currentPlan === 'free') return false;
    if (!expiry) return true;
    return expiry.getTime() > Date.now();
  }, [currentPlan, expiry]);

  // Refund is offered only within 24h of activation. We derive activation date
  // from expiry - 30d — this matches the backend's PeriodDays default.
  const refundEligible = useMemo(() => {
    if (!expiry || !hasActivePaid) return false;
    const activatedMs = expiry.getTime() - 30 * 24 * 60 * 60 * 1000;
    return Date.now() - activatedMs <= 24 * 60 * 60 * 1000;
  }, [expiry, hasActivePaid]);

  const [catalog, setCatalog] = useState<PricingCatalog | null>(null);
  const [loadingPlan, setLoadingPlan] = useState<PricingPlan | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      const country = await detectClientCountry(controller.signal);
      try {
        const c = await getPricing(country || undefined);
        if (!cancelled) setCatalog(c);
      } catch {
        if (!cancelled) setFetchError(true);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const handleCheckout = async (plan: Exclude<PricingPlan, 'free'>) => {
    if (!catalog) return;
    setLoadingPlan(plan);
    try {
      const res = await initializePayment({ plan, region: catalog.region || undefined });
      window.location.href = res.authorization_url;
    } catch (err) {
      toast.error((err as Error).message || 'Could not start checkout');
      setLoadingPlan(null);
    }
  };

  const handleCancel = async () => {
    setCanceling(true);
    try {
      const result = await cancelPlan();
      if (user) setUser({ ...user, plan: 'free', plan_expiry: null });
      if (result.refunded) {
        toast.success('Plan canceled and refund queued. Funds return to your card in a few days.');
      } else {
        toast.success('Plan canceled. You can resubscribe anytime.');
      }
      setCancelOpen(false);
    } catch (err) {
      toast.error((err as Error).message || 'Cancel failed');
    } finally {
      setCanceling(false);
    }
  };

  return (
    <AppLayout>
      <main className="flex-1 overflow-y-auto px-6 py-8">
        <div className="mx-auto max-w-5xl space-y-8">
          <PageHeader
            title="Billing"
            description="Pick a plan that fits your workflow. Cancel anytime."
            backTo="/dashboard"
          />


          <section className="rounded-clyra-lg border border-line-default bg-clyra-raised/60 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="text-micro uppercase tracking-wider text-ink-faint">Current plan</span>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-sm capitalize text-ink-primary">{currentPlan}</span>
                  {hasActivePaid && (
                    <span className="rounded-full border border-success/30 bg-success/10 px-1.5 py-0.5 text-micro text-success">
                      Active
                    </span>
                  )}
                </div>
                {expiry && hasActivePaid && (
                  <p className="mt-1 text-petit text-ink-muted">
                    Renews {expiry.toLocaleDateString()}
                  </p>
                )}
              </div>
              {hasActivePaid && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setCancelOpen(true)}
                >
                  Cancel plan
                </Button>
              )}
            </div>
          </section>

          {fetchError && (
            <p className="text-petit text-danger">Couldn't load pricing. Refresh and try again.</p>
          )}

          {catalog && (
            <section>
              <h2 className="text-sm font-medium text-ink-primary">Plans</h2>
              <p className="mt-1 text-petit text-ink-muted">
                {catalog.region === 'NG' ? 'Priced in naira.' : 'Priced in USD.'}
                {hasActivePaid && ' Cancel your current plan to switch or resubscribe.'}
              </p>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                {catalog.plans.map((plan) => (
                  <PlanCard
                    key={plan.plan}
                    plan={plan}
                    current={plan.plan === currentPlan && (plan.plan === 'free' || hasActivePaid)}
                    locked={hasActivePaid && plan.plan !== currentPlan && plan.plan !== 'free'}
                    loading={loadingPlan === plan.plan}
                    onSelect={() => {
                      if (plan.plan === 'free' || plan.plan === currentPlan || hasActivePaid) return;
                      handleCheckout(plan.plan);
                    }}
                  />
                ))}
              </div>
              <p className="mt-3 text-micro text-ink-faint">
                {catalog.region === 'NG'
                  ? 'Payments handled by Paystack.'
                  : 'Payments handled by Polar (Merchant of Record) — VAT/sales tax included where applicable.'}
                {' '}By subscribing you agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:text-brand-300">Terms</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:text-brand-300">Privacy Policy</a>.
              </p>
            </section>
          )}

          <section>
            <h2 className="text-sm font-medium text-ink-primary">Billing history</h2>
            <div className="mt-3 rounded-clyra-lg border border-line-default bg-clyra-raised/60 p-5">
              <PaymentHistory />
            </div>
          </section>
        </div>
      </main>

      <ConfirmDialog
        open={cancelOpen}
        title="Cancel your plan?"
        body={
          refundEligible
            ? "You're within the 24-hour grace window, so we'll queue a full refund to your card after downgrading to Free. Settlement takes a few days on your bank's side."
            : "Your plan will drop back to Free immediately. Past the 24-hour window we don't refund the partial month, so you'll lose any remaining paid days. You can resubscribe anytime."
        }
        confirmLabel={refundEligible ? 'Cancel & refund' : 'Cancel plan'}
        confirmVariant="danger"
        loading={canceling}
        onConfirm={handleCancel}
        onClose={() => setCancelOpen(false)}
      />
    </AppLayout>
  );
}

function PlanCard({
  plan,
  current,
  locked,
  loading,
  onSelect,
}: {
  plan: PricingPlanCard;
  current: boolean;
  locked: boolean;
  loading: boolean;
  onSelect: () => void;
}) {
  const highlight = plan.recommended;
  const isFree = plan.plan === 'free';

  return (
    <div
      className={`relative flex flex-col rounded-clyra-lg border p-5 transition-all ${
        highlight
          ? 'border-brand-500/50 bg-brand-500/[0.05] shadow-clyra'
          : 'border-line-default bg-clyra-raised/60'
      } ${locked ? 'opacity-60' : ''}`}
    >
      {highlight && (
        <span className="absolute -top-2.5 left-5 rounded-full border border-brand-500/40 bg-clyra-bg px-2 py-0.5 text-micro font-medium text-brand-300">
          Recommended
        </span>
      )}
      <div className="flex items-start justify-between">
        <h3 className="text-base font-semibold text-ink-primary">{plan.name}</h3>
        {current && (
          <span className="rounded-full border border-success/30 bg-success/10 px-1.5 py-0.5 text-micro text-success">
            Current
          </span>
        )}
      </div>
      <p className="mt-1 text-petit text-ink-muted">{plan.tagline}</p>

      <div className="mt-4 flex items-end gap-1">
        <span className="text-2xl font-semibold text-ink-primary">{plan.amount_major}</span>
        {plan.amount_minor > 0 && (
          <span className="pb-1 text-micro text-ink-faint">/ {plan.period}</span>
        )}
      </div>

      <p className="mt-3 text-petit text-ink-secondary">
        {plan.daily_limit.toLocaleString()} generations/day
      </p>

      <ul className="mt-3 space-y-1.5 text-petit text-ink-secondary">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-brand-400" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-5">
        {current ? (
          <Button variant="secondary" size="sm" className="w-full" disabled>
            You're on this plan
          </Button>
        ) : isFree ? (
          <Button variant="secondary" size="sm" className="w-full" disabled>
            Included by default
          </Button>
        ) : locked ? (
          <Button variant="secondary" size="sm" className="w-full" disabled>
            Cancel current plan to switch
          </Button>
        ) : (
          <Button size="sm" className="w-full" loading={loading} onClick={onSelect}>
            {plan.cta}
          </Button>
        )}
        {plan.requires_keys && (
          <p className="mt-2 text-micro text-ink-faint">Uses your own AI keys.</p>
        )}
      </div>
    </div>
  );
}
